<template>
  <v-dialog
    aria-label="Share recipe"
    :model-value="shareDialog"
    :fullscreen="true"
    transition="slide-x-reverse-transition"
  >
    <v-card>
      <v-toolbar color="surface">
        <v-btn :icon="true" dark @click="$emit('close')" aria-label="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>Sharing</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn variant="text" @click="share(shared)"> Save</v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-list lines="two" subheader>
        <v-list-subheader>Public link</v-list-subheader>
        <v-list-item
          title="Public"
          subtitle="When enabling sharing, your recipe will become visible to anyone with the link!"
        >
          <template v-slot:prepend>
            <v-checkbox v-model="shared" />
          </template>
        </v-list-item>
        <v-list-item v-if="shared">
          <v-text-field
            variant="outlined"
            :model-value="shareUrl"
            :readonly="true"
            prepend-icon="mdi-share"
            append-icon="mdi-content-copy"
            @click:append="copyPublicUrl"
          />
        </v-list-item>
      </v-list>
      <v-list lines="two" subheader>
        <v-list-subheader>Share with a person</v-list-subheader>
        <v-list-item
          subtitle="Generate a link to share this recipe with a specific person. They will see it in their recipe list."
        >
          <template v-slot:append>
            <v-btn
              v-if="!personalShareUrl"
              variant="tonal"
              :loading="generatingLink"
              @click="generatePersonalLink"
            >
              Generate link
            </v-btn>
          </template>
        </v-list-item>
        <v-list-item v-if="personalShareUrl">
          <v-text-field
            variant="outlined"
            :model-value="personalShareUrl"
            :readonly="true"
            prepend-icon="mdi-link"
            append-icon="mdi-content-copy"
            @click:append="copyPersonalUrl"
          />
        </v-list-item>
        <v-list-item
          v-for="profile in sharedWithProfiles"
          :key="profile.uid"
          :title="profile.displayName || profile.email"
        >
          <template v-slot:prepend>
            <v-avatar size="32">
              <v-img v-if="profile.photoUrl" :src="profile.photoUrl" alt="" />
              <v-icon v-else icon="mdi-account" />
            </v-avatar>
          </template>
          <template v-slot:append>
            <v-btn
              icon="mdi-close"
              variant="text"
              :aria-label="`Stop sharing with ${profile.displayName || profile.email}`"
              @click="$emit('remove-share', profile.uid)"
            />
          </template>
        </v-list-item>
      </v-list>
    </v-card>
    <v-snackbar v-model="copiedToClipboard"> Copied to clipboard! </v-snackbar>
  </v-dialog>
</template>
<script lang="ts">
  import { defineComponent, type PropType, ref } from 'vue';
  import type { Recipe } from '@/service/recipe/types.ts';
  import type { Profile } from '@/service/profile/types';

  export default defineComponent({
    name: 'ShareDialog',
    props: {
      recipe: {
        type: Object as PropType<Recipe>,
        required: true,
      },
      share: {
        type: Function as PropType<(share: boolean) => void>,
        required: true,
      },
      shareDialog: {
        type: Boolean,
        required: true,
      },
      sharedWithProfiles: {
        type: Array as PropType<Profile[]>,
        default: () => [],
      },
    },
    emits: ['close', 'generate-recipe-invite', 'remove-share'],
    setup(props, { emit }) {
      const shared = ref(props.recipe.shared);
      const copiedToClipboard = ref(false);
      const shareUrl = `${location.origin}/share/${props.recipe.id}`;
      const personalShareUrl = ref<string | undefined>(undefined);
      const generatingLink = ref(false);

      return {
        shared,
        shareUrl,
        copiedToClipboard,
        personalShareUrl,
        generatingLink,
        copyPublicUrl() {
          copiedToClipboard.value = true;
          navigator.clipboard.writeText(shareUrl);
        },
        copyPersonalUrl() {
          if (personalShareUrl.value) {
            copiedToClipboard.value = true;
            navigator.clipboard.writeText(personalShareUrl.value);
          }
        },
        generatePersonalLink() {
          generatingLink.value = true;
          emit('generate-recipe-invite', (url: string) => {
            personalShareUrl.value = url;
            generatingLink.value = false;
          });
        },
      };
    },
  });
</script>
